import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAppPermissions } from '../hooks/useAppPermissions';

/**
 * ADR-018 §5 — Lente operacional ativa (lands | launches).
 *
 * Ajuste 2 do mini-Council Gemini: availableUnits = null enquanto permissions
 * ainda carregando (sentinela). Componentes que renderizam nav escopada por
 * unit devem checar isLoading antes de decidir.
 *
 * Persiste unit ativa em localStorage para sobreviver a refresh.
 */
const STORAGE_KEY = 'crm-inc.active_business_unit';
const KNOWN_UNITS = ['lands', 'launches'];

const BusinessUnitContext = createContext(null);

export function BusinessUnitProvider({ children }) {
  const { permissions, loading: permsLoading } = useAppPermissions();

  const availableUnits = useMemo(() => {
    if (permsLoading) return null;
    const set = new Set();
    permissions.forEach((p) => {
      const m = /^([a-z_]+)\.read$/.exec(p);
      if (m && KNOWN_UNITS.includes(m[1])) set.add(m[1]);
    });
    return Array.from(set);
  }, [permissions, permsLoading]);

  const isLoading = permsLoading || availableUnits === null;

  const [activeUnit, setActiveUnitState] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (isLoading || !availableUnits) return;
    if (availableUnits.length === 0) {
      setActiveUnitState(null);
      return;
    }
    if (!activeUnit || !availableUnits.includes(activeUnit)) {
      const next = availableUnits.includes('lands') ? 'lands' : availableUnits[0];
      setActiveUnitState(next);
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    }
  }, [isLoading, availableUnits, activeUnit]);

  const setActiveUnit = useCallback((code) => {
    if (!availableUnits || !availableUnits.includes(code)) return;
    setActiveUnitState(code);
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* noop */ }
  }, [availableUnits]);

  const value = useMemo(() => ({
    activeUnit,
    availableUnits,
    setActiveUnit,
    isLoading,
  }), [activeUnit, availableUnits, setActiveUnit, isLoading]);

  return (
    <BusinessUnitContext.Provider value={value}>
      {children}
    </BusinessUnitContext.Provider>
  );
}

export function useBusinessUnit() {
  const ctx = useContext(BusinessUnitContext);
  if (ctx === null) {
    throw new Error('useBusinessUnit deve ser usado dentro de <BusinessUnitProvider>');
  }
  return ctx;
}
